import { useEffect, useState } from 'react';
import { ReactSVG } from 'react-svg';
import { Switch } from 'antd';
import { toast } from 'common/helpers/toastConfig';
import TextInput from 'components/form/textInput';
import LineWrappedComponent from 'components/lineWrappedComponent';
import DropDown from 'components/form/dropDown';
import { menuCustomizationArray } from 'common/enums';
import Gap from 'components/gap';
import Table from 'pages/panel/cafeteria/customizations/lib/create/lib/table/index';
import { useActions } from 'common/hooks/useActions';
import { useNavigate } from 'react-router';
import { useTypedSelector } from 'common/hooks/useTypedSelector';
import { useParams } from 'react-router';
import { Modal } from 'antd';

import * as S from './styles';

export default function CafeteriaCustomizationCreate() {
  const [isActive, setIsActive] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [updateIndex, setUpdateIndex] = useState<number | null>();
  const [values, setValues] = useState<any>({
    title: '',
    type: '',
    typeTitle: '',
    option: '',
    defaultOption: false,
    defaultOptionIndex: null,
    translations: [
      {
        language: 'en',
        translate: ''
      },
      {
        language: 'ar',
        translate: ''
      }
    ],
    titleTranslations: [
      {
        language: 'en',
        translate: ''
      },
      {
        language: 'ar',
        translate: ''
      }
    ]
  });
  const [items, setItems] = useState<any[]>([]);

  const { menuCustomizationCreateRequestAction, MenuCustomDetailRequestAction, menuCustomizationUpdateRequestAction } =
    useActions();

  const navigate = useNavigate();

  const showModal = (e: any) => {
    e.preventDefault();
    setOpen(true);
  };

  const removeDeletedItems = (items: any[]) => {
    return items.filter((item, index, array) => {
      if (item.deleted) return false;

      const isDuplicate = array.some(
        (otherItem, otherIndex) =>
          otherIndex !== index && JSON.stringify(otherItem.translations) === JSON.stringify(item.translations)
      );

      return !isDuplicate;
    });
  };

  const handleOk = () => {
    let requestBody = {};

    if (values?.titleTranslations?.find((t: any) => t.language === 'en')?.translate.length < 1) {
      toast({ model: 'error', message: 'Please enter an English title' });
      setOpen(false);
      return;
    } else if (values?.titleTranslations?.find((t: any) => t.language === 'ar')?.translate.length < 1) {
      toast({ model: 'error', message: 'Please enter an Arabic title' });
      setOpen(false);
      return;
    } else if (values.type === 'SINGLE_SELECT' && items.length < 1) {
      toast({ model: 'error', message: 'Please enter at least one option' });
      setOpen(false);
      return;
    } else if (values.type === 'SINGLE_SELECT' && values.defaultOptionIndex === null) {
      toast({ model: 'error', message: 'Please select one option as the default' });
      setOpen(false);
      return;
    }

    if (values.type === 'SINGLE_SELECT') {
      requestBody = {
        isActive: isActive,
        translations: values.titleTranslations,
        optionType: 'SINGLE_SELECT',
        items: items,
        defaultData: {
          optionItemValue: [values.defaultOptionIndex]
        }
      };
    } else if (values.type === 'BOOLEAN') {
      requestBody = {
        isActive: isActive,
        translations: values.titleTranslations,
        optionType: 'BOOLEAN',
        defaultData: {
          booleanValue: values.defaultOption
        }
      };
    }

    menuCustomizationUpdateRequestAction(requestBody, params?.id, () => navigate('/panel/cafeteria/customizations'));
  };

  const transformOptionItems = (optionItems: any[]) => {
    return optionItems.map((item) => ({
      translations: item.translations?.length
        ? item.translations.map((translation: any) => ({
            language: translation.language || '',
            translate: translation.translate || ''
          }))
        : [{ language: '', translate: '' }],
      id: item.id || null,
      deleted: !!item.deleted,
      isDefault: !!item.isDefault
    }));
  };

  const processServerResponse = (data: any) => {
    const optionItems = data.optionItems || [];

    const options = optionItems.map((item: any) => ({
      options: item.translations[0]?.translate || ''
    }));

    const defaultOptionIndex = optionItems.findIndex((item: any) => item.isDefault);

    setOptions(options);
    setValues((prevValues: any) => ({
      ...prevValues,
      defaultOptionIndex: defaultOptionIndex !== -1 ? defaultOptionIndex : null
    }));
  };

  const params: any = useParams();

  const { CustomItem }: any = useTypedSelector((state) => state.Cafeteria);

  const optionsHandler = (e: any) => {
    e.preventDefault();

    const englishTranslation = values.translations.find((t: any) => t.language === 'en')?.translate;
    const arabicTranslation = values.translations.find((t: any) => t.language === 'ar')?.translate;

    if (!englishTranslation || !arabicTranslation) {
      toast({
        model: 'error',
        message: !englishTranslation ? 'Please enter an English option' : 'Please enter an Arabic option'
      });
      return;
    }

    if (options.length >= 10 || items.length >= 10) {
      toast({ model: 'error', message: 'You can add up to 10 options only' });
      return;
    }

    const newOption = { options: englishTranslation };
    const newItem = {
      translations: [
        { language: 'en', translate: englishTranslation },
        { language: 'ar', translate: arabicTranslation }
      ],
      id: null,
      deleted: false,
      isDefault: false
    };

    setOptions([...options, newOption]);
    setItems([...items, newItem]);

    setValues((prev: any) => ({
      ...prev,
      option: '',
      translations: prev.translations.map((t: any) => ({ ...t, translate: '' }))
    }));
  };

  const handleDelete = (e: any, item: any) => {
    e.preventDefault();

    const updatedOptions = options.filter((option) => option.options !== item?.translations);

    setOptions(updatedOptions);

    const updatedItems = items?.map((i) => {
      if (
        i.translations?.[0]?.translate === item?.translations?.[0]?.translate &&
        i.translations?.[1]?.translate === item?.translations?.[1]?.translate
      ) {
        return { ...i, deleted: true, isDefault: false };
      }
      return i;
    });

    setItems(updatedItems);

    toast({ model: 'success', message: 'Option deleted successfully' });
  };

  const handleSelect = (e: any, index: number | null, item: any) => {
    e.preventDefault();

    const updatedItems = items.map((i) => ({
      ...i,
      isDefault:
        i.translations?.[0]?.translate === item?.translations?.[0]?.translate &&
        i.translations?.[1]?.translate === item?.translations?.[1]?.translate
    }));

    setItems(updatedItems);
  };

  const handleOptionsChange = (e: any, item: any) => {
    e.preventDefault();
    const { name, value } = e.target;

    const updatedItems = items?.map((i) => {
      if (
        i.translations?.[0]?.translate === item?.translations?.[0]?.translate &&
        i.translations?.[1]?.translate === item?.translations?.[1]?.translate
      ) {
        let updatedItem = { ...i };

        if (name === 'en-title') {
          updatedItem.translations = updatedItem.translations.map((t: any) =>
            t.language === 'en' ? { ...t, translate: value } : t
          );
        } else if (name === 'ar-title') {
          updatedItem.translations = updatedItem.translations.map((t: any) =>
            t.language === 'ar' ? { ...t, translate: value } : t
          );
        }

        return updatedItem;
      }
      return i;
    });

    setItems(updatedItems);
  };

  const transformItems = (input: any) => {
    return {
      items: input.map((group: any) => ({
        translations: group.map((translation: any) => ({
          language: translation.language,
          translate: translation.translate
        }))
      }))
    };
  };

  const submitHandler = (e: any) => {
    e.preventDefault();

    if (values?.titleTranslations?.find((t: any) => t.language === 'en')?.translate.length < 1) {
      toast({ model: 'error', message: 'Please enter an English title' });
    } else if (values?.titleTranslations?.find((t: any) => t.language === 'ar')?.translate.length < 1) {
      toast({ model: 'error', message: 'Please enter an Arabic title' });
    } else if (values.type === 'SINGLE_SELECT' && items.length < 1) {
      toast({ model: 'error', message: 'Please enter at least one option' });
      return;
    } else if (values.type === 'SINGLE_SELECT' && values.defaultOptionIndex === null) {
      toast({ model: 'error', message: 'Please select one option as the default' });
      return;
    }

    const transformedData = removeDeletedItems(items)?.map((item) => item.translations);

    let requestData = {};

    if (values.type === 'SINGLE_SELECT') {
      requestData = {
        isActive: isActive,
        translations: values.titleTranslations,
        optionType: 'SINGLE_SELECT',
        items: transformItems(transformedData)?.items,
        defaultData: {
          optionItemValue: [values.defaultOptionIndex]
        }
      };
    } else if (values.type === 'BOOLEAN') {
      requestData = {
        isActive: isActive,
        translations: values.titleTranslations,
        optionType: 'BOOLEAN',
        defaultData: {
          booleanValue: values.defaultOption
        }
      };
    }

    menuCustomizationCreateRequestAction(requestData, () => navigate('/panel/cafeteria/customizations'));
  }; 

  const simplifyTranslations = (translations: any[]) => {
    return translations.map((translation) => ({
      language: translation.language || '',
      translate: translation.translate || ''
    }));
  };

  useEffect(() => {
    if (params?.id) MenuCustomDetailRequestAction(params?.id);
  }, [params]);

  useEffect(() => {
    if (params?.id && CustomItem) {
      setIsActive(CustomItem?.data?.list?.isActive);

      if (CustomItem?.data?.list?.optionType === 'BOOLEAN') {
        setValues({
          type: CustomItem?.data?.list?.optionType,
          typeTitle: 'Yes/No',
          titleTranslations: simplifyTranslations(CustomItem?.data?.list?.translations),
          defaultOption: CustomItem?.data?.list?.defaultData?.booleanValue
        });
      } else if (CustomItem?.data?.list?.optionType === 'SINGLE_SELECT') {
        setValues({
          type: CustomItem?.data?.list?.optionType,
          typeTitle: 'Single Select',
          titleTranslations: simplifyTranslations(CustomItem?.data?.list?.translations),
          translations: [
            {
              language: 'en',
              translate: ''
            },
            {
              language: 'ar',
              translate: ''
            }
          ]
        });
        processServerResponse(CustomItem?.data?.list);
        setItems(transformOptionItems(CustomItem?.data?.list?.optionItems));
      }
    }
  }, [params?.id, CustomItem]);

  useEffect(() => {
    setValues({ ...values, defaultOptionIndex: updateIndex });
  }, [updateIndex]);

  return (
    <S.Container>
      <S.Head>
        <S.Title>{`${params?.id ? 'Update' : 'Create New'} Customization`}</S.Title>
        <S.SwitchContainer>
          <S.SwitchTitle style={{ color: isActive ? '#1DBAAF' : '#FF6464' }}>
            {isActive ? 'Active' : 'InActive'}
          </S.SwitchTitle>
          <Switch
            checked={isActive}
            size="default"
            onChange={() => setIsActive(!isActive)}
            style={{
              backgroundColor: isActive ? '#1DBAAF' : '#FF6464'
            }}
          />
        </S.SwitchContainer>
      </S.Head>
      <Gap size="20px" />
      <S.Div className="row">
        <S.Div className="col-md-12">
          <form onSubmit={params?.id ? showModal : submitHandler} style={{ paddingBottom: '2rem' }}>
            <S.Div style={{ width: '50%', paddingRight: '1rem' }}>
              <DropDown
                label="Choose Type"
                list={menuCustomizationArray}
                placeholder="Option Type"
                onChange={(e) => {
                  setValues({ ...values, typeTitle: e.title, type: e.id });
                }}
                defaultValue={{ title: values.typeTitle, id: values.type }}
                key={values.type}
              />
            </S.Div>
            {values.type !== '' && (
              <>
                <Gap size="15px" />
                <S.Div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '1.9rem' }}>
                  <TextInput
                    label="English Title"
                    name="en-title"
                    value={values?.titleTranslations?.find((t: any) => t.language === 'en')?.translate || ''}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        titleTranslations: values?.titleTranslations?.map((t: any) =>
                          t.language === 'en' ? { ...t, translate: e.target.value } : t
                        )
                      });
                    }}
                    placeholder="English Title ..."
                  />

                  <TextInput
                    label="Arabic Title"
                    name="ar-title"
                    value={values?.titleTranslations?.find((t: any) => t.language === 'ar')?.translate || ''}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        titleTranslations: values?.titleTranslations?.map((t: any) =>
                          t.language === 'ar' ? { ...t, translate: e.target.value } : t
                        )
                      });
                    }}
                    placeholder="العنوان باللغة العربية..."
                  />
                </S.Div>
              </>
            )}
            {values.type === 'BOOLEAN' && (
              <>
                <Gap size="15px" />
                <S.CustomCheckbox
                  checked={values.defaultOption}
                  onChange={(e) => {
                    setValues({ ...values, defaultOption: e.target.checked });
                  }}
                >
                  <span className="ant-checkbox-title">Default Option</span>
                </S.CustomCheckbox>
              </>
            )}
            {values.type === 'SINGLE_SELECT' && (
              <>
                <Gap size="15px" />
                <S.OptionsTitle>Options</S.OptionsTitle>
                <S.Div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '1.9rem' }}>
                  <TextInput
                    label="English Option"
                    name="en-option"
                    value={values?.translations?.find((t: any) => t.language === 'en')?.translate}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        translations: values?.translations?.map((t: any) =>
                          t.language === 'en' ? { ...t, translate: e.target.value } : t
                        )
                      });
                    }}
                    placeholder="Enter Option ..."
                    disabled={options.length === 10}
                  />

                  <TextInput
                    label="Arabic Option"
                    name="ar-option"
                    value={values?.translations?.find((t: any) => t.language === 'ar')?.translate}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        translations: values?.translations?.map((t: any) =>
                          t.language === 'ar' ? { ...t, translate: e.target.value } : t
                        )
                      });
                    }}
                    placeholder="أدخل الخيار..."
                    disabled={options.length === 10}
                  />
                </S.Div>
              </>
            )}
            {values.type === 'SINGLE_SELECT' && (
              <>
                <Gap size="30px" />
                <LineWrappedComponent lineHeight="1px">
                  <S.AddOption onClick={optionsHandler}>
                    <ReactSVG
                      src={'/icons/add-square.svg'}
                      beforeInjection={(svg) => {
                        svg.setAttribute('width', '24px');
                        svg.setAttribute('height', '24px');
                      }}
                    />
                    Add Option
                  </S.AddOption>
                </LineWrappedComponent>
              </>
            )}
            <Gap size="10px" />
            {values.type === 'SINGLE_SELECT' && items?.map((item) => item.deleted === false).includes(true) && (
              <Table
                data={items}
                onDelete={handleDelete}
                onSelect={handleSelect}
                defaultIndex={values.defaultOptionIndex}
                setUpdateIndex={setUpdateIndex}
                handleOptionsChange={handleOptionsChange}
              />
            )}
            {values.type !== '' && (
              <>
                <Gap size="20px" />
                <S.Button type="submit">{params?.id ? 'Update Customization' : 'Create Customization'}</S.Button>
              </>
            )}
          </form>
        </S.Div>
      </S.Div>

      <Modal title="Confirmation modal" open={open} onOk={handleOk} onCancel={() => setOpen(false)}>
        <p>Confirm that you want to update your custom menu</p>
      </Modal>
    </S.Container>
  );
}
